import { useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import {
  useAddAttackPlanTargets,
  useAddAttackPlanWave,
  useAttackPackageTemplates,
  useAttackPlanCheck,
  useAttackPlanDetail,
  useAttackPlanDsWorkbenchExport,
  useClearAttackPlanPool,
  useDeleteAttackPlanTarget,
  useDeleteAttackPlanWave,
  useImportAttackPlanPool,
  useRunAttackPlanCheck,
  useShiftAttackPlanArrivals,
  useUpdateAttackPlan,
  useWorldOptions,
} from '../api/queries';
import {
  ATTACK_PACKAGE_CATEGORY_LABELS,
  ATTACK_PLAN_STATUS_LABELS,
  type AttackPackageTemplate,
  type AttackPlan,
  type AttackPlanStatus,
  type AttackPlanTarget,
  type DsWorkbenchExportResult,
  type PoolVillage,
  type User,
  type WaveResult,
} from '../api/types';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';

function fromDatetimeLocal(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

function toDatetimeLocal(ms: number | null): string {
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function unitsToText(units: Record<string, number | undefined> | null): string {
  if (!units) return '';
  return Object.entries(units)
    .filter(([, count]) => (count ?? 0) > 0)
    .map(([unit, count]) => `${unit}:${count}`)
    .join(', ');
}

/**
 * Kriegsplan-Detailseite: Truppenpool (CSV-Paste), Zieldörfer & Wellen,
 * Verfügbarkeit & Export -- als einfache Sektionen auf einer Seite statt
 * eines Multi-Step-Wizards.
 */
export function AttackPlanDetailPage({ user }: { user: User }) {
  const { id } = useParams<{ id: string }>();
  const planId = Number(id);

  // In der URL statt in reinem Komponenten-State (explizite Nutzeranfrage:
  // ein Reload landete sonst wieder auf "Welt wählen …" und verlor den
  // gerade geöffneten Plan) -- gleiches Muster wie AdminUsersPage.tsx.
  const [searchParams, setSearchParams] = useSearchParams();
  const needsWorldChoice = user.world === null;
  const worldKey = searchParams.get('welt') ?? '';
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;

  const detail = useAttackPlanDetail(activeWorld || undefined, planId);
  const packageTemplates = useAttackPackageTemplates(activeWorld || undefined);
  const toast = useToast();

  if (needsWorldChoice && !worldKey) {
    return (
      <section className="card">
        <label className="field">
          <span>Welt</span>
          <select
            value={worldKey}
            onChange={(event) => setSearchParams({ welt: event.target.value }, { replace: true })}
            required
          >
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      </section>
    );
  }

  if (detail.isPending) {
    return (
      <section className="card">
        <Spinner label="Wird geladen …" />
      </section>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <section className="card">
        <div className="flash flash-error">
          {detail.error instanceof ApiError ? detail.error.message : 'Kriegsplan konnte nicht geladen werden.'}
        </div>
      </section>
    );
  }

  const { plan, targets, pool } = detail.data;
  const templates = packageTemplates.data?.templates ?? [];

  return (
    <>
      <PlanHeader plan={plan} templates={templates} worldKey={activeWorld} toast={toast} />
      <PoolSection planId={planId} worldKey={activeWorld} pool={pool} templates={templates} toast={toast} />
      <TargetsSection
        planId={planId}
        worldKey={activeWorld}
        targets={targets}
        templates={templates}
        lastWaveArrivalStart={plan.lastWaveArrivalStart}
        lastWaveArrivalEnd={plan.lastWaveArrivalEnd}
        toast={toast}
      />
      <CheckSection planId={planId} worldKey={activeWorld} lastCheckedAt={plan.lastCheckedAt} toast={toast} />
    </>
  );
}

function PlanHeader({
  plan,
  templates,
  worldKey,
  toast,
}: {
  plan: AttackPlan;
  templates: AttackPackageTemplate[];
  worldKey: string;
  toast: ReturnType<typeof useToast>;
}) {
  const update = useUpdateAttackPlan(worldKey);

  const [wavesPerTarget, setWavesPerTarget] = useState(String(plan.wavesPerTarget ?? ''));
  const [fakePackageTemplateId, setFakePackageTemplateId] = useState(String(plan.fakePackageTemplateId ?? ''));
  const [fakeArrivalStart, setFakeArrivalStart] = useState(toDatetimeLocal(plan.fakeArrivalStart));
  const [fakeArrivalEnd, setFakeArrivalEnd] = useState(toDatetimeLocal(plan.fakeArrivalEnd));

  const saveKattaSettings = (event: FormEvent) => {
    event.preventDefault();
    if (!wavesPerTarget || !fakePackageTemplateId) {
      toast.push('error', 'Bitte Wellenzahl je Ziel und Fake-Pakettyp angeben.');
      return;
    }
    update.mutate(
      {
        id: plan.id,
        wavesPerTarget: Number(wavesPerTarget),
        fakePackageTemplateId: Number(fakePackageTemplateId),
        fakeArrivalStart: fromDatetimeLocal(fakeArrivalStart),
        fakeArrivalEnd: fromDatetimeLocal(fakeArrivalEnd),
      },
      {
        onSuccess: () => toast.push('success', 'Katta-Einstellungen gespeichert.'),
        onError: (err) => toast.push('error', err.message),
      },
    );
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>{plan.name}</h1>
          <p className="muted">{plan.mode === 'katta' ? 'Katta-Kampagne (mit Fake-Auffüllung)' : 'Manueller Plan'}</p>
        </div>
        <label className="field" style={{ minWidth: '180px' }}>
          <span>Status</span>
          <select
            value={plan.status}
            disabled={update.isPending}
            onChange={(e) => update.mutate({ id: plan.id, status: e.target.value as AttackPlanStatus }, { onError: (err) => toast.push('error', err.message) })}
          >
            {Object.entries(ATTACK_PLAN_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {plan.mode === 'katta' && (
        <form className="form" onSubmit={saveKattaSettings} noValidate style={{ marginTop: '16px' }}>
          <h3 style={{ margin: 0 }}>Katta-Einstellungen</h3>
          <div className="field-row">
            <label className="field">
              <span>Wellen je Zieldorf (insgesamt, inkl. Fakes)</span>
              <input type="number" min={1} value={wavesPerTarget} onChange={(e) => setWavesPerTarget(e.target.value)} required />
            </label>
            <label className="field grow">
              <span>Pakettyp für Fakes</span>
              <select value={fakePackageTemplateId} onChange={(e) => setFakePackageTemplateId(e.target.value)} required>
                <option value="" disabled>
                  Pakettyp wählen …
                </option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="field-row">
            <label className="field">
              <span>Fake-Ankunft ab (optional)</span>
              <input type="datetime-local" step="1" value={fakeArrivalStart} onChange={(e) => setFakeArrivalStart(e.target.value)} />
            </label>
            <label className="field">
              <span>Fake-Ankunft bis (optional)</span>
              <input type="datetime-local" step="1" value={fakeArrivalEnd} onChange={(e) => setFakeArrivalEnd(e.target.value)} />
            </label>
          </div>
          <div className="button-row">
            <button type="submit" className="btn btn-sm" disabled={update.isPending}>
              {update.isPending ? 'Wird gespeichert …' : 'Katta-Einstellungen speichern'}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

interface PackageCapacity {
  template: AttackPackageTemplate;
  /** Pakete, die der gesamte Pool von diesem Typ hergäbe, wenn alle Truppen nur dafür genutzt würden. */
  packages: number;
  /** Dörfer, die mindestens ein Paket dieses Typs stellen können. */
  villages: number;
}

/**
 * Wie viele Pakete je Pakettyp aus dem Truppenpool möglich sind: je Dorf der
 * kleinste Quotient (Bestand / Mindestbedarf) über alle geforderten Einheiten,
 * aufsummiert. Mit "Kein Splitting" (nur Angriff/Wallbreaker) stellt ein Dorf
 * höchstens EIN Paket. Jeder Typ wird für sich gerechnet -- die Typen teilen
 * sich dieselben Truppen, die Zahlen sind also nicht addierbar.
 */
function computePackageCapacities(pool: PoolVillage[], templates: AttackPackageTemplate[]): PackageCapacity[] {
  const result: PackageCapacity[] = [];
  for (const template of templates) {
    const required = Object.entries(template.minUnits).filter(([, need]) => Number(need) > 0);
    if (required.length === 0) continue;
    const splitProtected = template.preventSplitPerVillage && (template.category === 'attack' || template.category === 'wallbreaker');

    let packages = 0;
    let villages = 0;
    for (const village of pool) {
      let count = Math.min(...required.map(([unit, need]) => Math.floor((Number(village.units[unit as keyof typeof village.units]) || 0) / Number(need))));
      if (splitProtected) count = Math.min(count, 1);
      if (count > 0) {
        packages += count;
        villages += 1;
      }
    }
    result.push({ template, packages, villages });
  }
  return result;
}

function PoolSection({
  planId,
  worldKey,
  pool,
  templates,
  toast,
}: {
  planId: number;
  worldKey: string;
  pool: PoolVillage[];
  templates: AttackPackageTemplate[];
  toast: ReturnType<typeof useToast>;
}) {
  const capacities = computePackageCapacities(pool, templates);
  const [csvText, setCsvText] = useState('');
  const importPool = useImportAttackPlanPool(worldKey, planId);
  const clearPool = useClearAttackPlanPool(worldKey, planId);

  const handleImport = (event: FormEvent) => {
    event.preventDefault();
    if (!csvText.trim()) return;
    importPool.mutate(
      { csvText },
      {
        onSuccess: (result) => {
          const info = result.errors.length
            ? `⚠️ ${result.imported} Dörfer importiert, ${result.errors.length} Zeile(n) übersprungen.`
            : `${result.imported} Dörfer importiert.`;
          toast.push(result.errors.length ? 'error' : 'success', info);
          setCsvText('');
        },
        onError: (err) => toast.push('error', err.message),
      },
    );
  };

  const handleClear = () => {
    if (!window.confirm('Truppenpool wirklich vollständig leeren?')) return;
    clearPool.mutate(undefined, {
      onSuccess: () => toast.push('success', 'Truppenpool wurde geleert.'),
      onError: (err) => toast.push('error', err.message),
    });
  };

  return (
    <section className="card">
      <h2>Truppenpool</h2>
      <p className="muted">
        CSV aus dem Userskript-Export ("Stamm → Mitglieder → Verteidigung") hier einfügen. Ein erneuter Import überschreibt
        nur die genannten Dörfer, andere bleiben unangetastet.
      </p>

      <form className="form" onSubmit={handleImport} noValidate>
        <label className="field">
          <span>CSV-Text</span>
          <textarea rows={4} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder="Coords,Player,label,spear,sword,axe,…" />
        </label>
        <div className="button-row">
          <button type="submit" className="btn btn-primary" disabled={importPool.isPending || !csvText.trim()}>
            {importPool.isPending ? 'Wird importiert …' : 'Importieren'}
          </button>
          <button type="button" className="btn btn-danger" disabled={clearPool.isPending || pool.length === 0} onClick={handleClear}>
            Pool leeren
          </button>
        </div>
      </form>

      {pool.length === 0 && <p className="muted center pad">Noch keine Dörfer im Truppenpool.</p>}
      {pool.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Dorf</th>
                <th>Koordinate</th>
                <th>Spieler</th>
                <th>Einheiten</th>
              </tr>
            </thead>
            <tbody>
              {pool.map((village) => (
                <tr key={village.id}>
                  <td>{village.label}</td>
                  <td className="nowrap">
                    {village.x}|{village.y}
                  </td>
                  <td>{village.player ?? <span className="muted">unbekannt</span>}</td>
                  <td>{unitsToText(village.units)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pool.length > 0 && (
        <>
          <h3 style={{ marginBottom: 4 }}>Mögliche Pakete aus dem Truppenpool</h3>
          {capacities.length === 0 ? (
            <p className="muted">Noch keine Pakettyp-Vorlagen angelegt.</p>
          ) : (
            <>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Pakettyp</th>
                      <th>Kategorie</th>
                      <th>Mindest-Einheiten</th>
                      <th className="right">Pakete möglich</th>
                      <th className="right">aus Dörfern</th>
                    </tr>
                  </thead>
                  <tbody>
                    {capacities.map(({ template, packages, villages }) => (
                      <tr key={template.id}>
                        <td>{template.name}</td>
                        <td>{ATTACK_PACKAGE_CATEGORY_LABELS[template.category]}</td>
                        <td>
                          {unitsToText(template.minUnits)}
                          {template.preventSplitPerVillage && <span className="muted"> · max. 1 je Dorf</span>}
                        </td>
                        <td className="right">
                          <strong>{packages.toLocaleString('de-DE')}</strong>
                        </td>
                        <td className="right">{villages.toLocaleString('de-DE')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="muted">
                Jeder Pakettyp ist einzeln gerechnet: die Typen teilen sich dieselben Truppen, die Werte lassen sich daher nicht
                addieren.
              </p>
            </>
          )}
        </>
      )}
    </section>
  );
}

function TargetsSection({
  planId,
  worldKey,
  targets,
  templates,
  lastWaveArrivalStart,
  lastWaveArrivalEnd,
  toast,
}: {
  planId: number;
  worldKey: string;
  targets: AttackPlanTarget[];
  templates: Array<{ id: number; name: string }>;
  lastWaveArrivalStart: number | null;
  lastWaveArrivalEnd: number | null;
  toast: ReturnType<typeof useToast>;
}) {
  const addTargets = useAddAttackPlanTargets(worldKey, planId);
  const deleteTarget = useDeleteAttackPlanTarget(worldKey, planId);
  const addWave = useAddAttackPlanWave(worldKey, planId);
  const deleteWave = useDeleteAttackPlanWave(worldKey, planId);
  const shiftArrivals = useShiftAttackPlanArrivals(worldKey, planId);

  const [coordsText, setCoordsText] = useState('');
  const [withWave, setWithWave] = useState(false);
  const [packageTemplateId, setPackageTemplateId] = useState('');
  const [count, setCount] = useState('1');
  // Vorbelegt mit der zuletzt beim Hinzufügen einer Welle benutzten
  // Ankunftszeitspanne (explizite Nutzeranfrage) -- nur beim ersten Rendern
  // gesetzt, damit ein späteres Neuladen der Plan-Query laufende Eingaben
  // nicht überschreibt.
  const [arrivalStart, setArrivalStart] = useState(() => toDatetimeLocal(lastWaveArrivalStart));
  const [arrivalEnd, setArrivalEnd] = useState(() => toDatetimeLocal(lastWaveArrivalEnd));
  const [critical, setCritical] = useState(false);
  const [shiftMinutes, setShiftMinutes] = useState('');

  const handleAddTargets = (event: FormEvent) => {
    event.preventDefault();
    if (!coordsText.trim()) return;
    const start = fromDatetimeLocal(arrivalStart);
    if (withWave && (!packageTemplateId || start === null)) {
      toast.push('error', 'Bitte Pakettyp und Ankunftszeit angeben.');
      return;
    }
    addTargets.mutate(
      {
        coordsText,
        wave: withWave
          ? {
              packageTemplateId: Number(packageTemplateId),
              count: Number(count),
              arrivalStart: start!,
              arrivalEnd: fromDatetimeLocal(arrivalEnd),
              critical,
            }
          : undefined,
      },
      {
        onSuccess: (result) => {
          toast.push('success', `${result.created} neue(s) Zieldorf/Zieldörfer, ${result.reused} bereits vorhanden.`);
          setCoordsText('');
        },
        onError: (err) => toast.push('error', err.message),
      },
    );
  };

  const handleShift = () => {
    const minutes = Number(shiftMinutes);
    if (!Number.isFinite(minutes) || minutes === 0) {
      toast.push('error', 'Bitte eine Minutenzahl ungleich 0 angeben.');
      return;
    }
    shiftArrivals.mutate(
      { shiftMinutes: minutes },
      {
        onSuccess: (result) => {
          toast.push('success', `${result.shifted} Welle(n) verschoben.`);
          setShiftMinutes('');
        },
        onError: (err) => toast.push('error', err.message),
      },
    );
  };

  return (
    <section className="card">
      <h2>Zieldörfer & Wellen</h2>

      <form className="form" onSubmit={handleAddTargets} noValidate>
        <label className="field">
          <span>Koordinaten (mehrere möglich, mit Leerzeichen oder Zeilenumbruch getrennt)</span>
          <textarea rows={2} value={coordsText} onChange={(e) => setCoordsText(e.target.value)} placeholder="512|546 513|547" required />
        </label>

        <label className="checkbox-field">
          <input type="checkbox" checked={withWave} onChange={(e) => setWithWave(e.target.checked)} />
          <span>Gleich eine Welle für jedes betroffene Ziel anlegen</span>
        </label>

        {withWave && (
          <>
            <div className="field-row">
              <label className="field grow">
                <span>Pakettyp</span>
                <select value={packageTemplateId} onChange={(e) => setPackageTemplateId(e.target.value)} required>
                  <option value="" disabled>
                    Pakettyp wählen …
                  </option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Anzahl</span>
                <input type="number" min={1} value={count} onChange={(e) => setCount(e.target.value)} required />
              </label>
            </div>
            <div className="field-row">
              <label className="field">
                <span>Ankunft ab</span>
                <input type="datetime-local" step="1" value={arrivalStart} onChange={(e) => setArrivalStart(e.target.value)} required />
              </label>
              <label className="field">
                <span>Ankunft bis (optional, streut Pakete darüber)</span>
                <input type="datetime-local" step="1" value={arrivalEnd} onChange={(e) => setArrivalEnd(e.target.value)} />
              </label>
            </div>
            <label className="checkbox-field">
              <input type="checkbox" checked={critical} onChange={(e) => setCritical(e.target.checked)} />
              <span>Kritisch (alle Pakete dieser Welle oder gar keins)</span>
            </label>
          </>
        )}

        <div className="button-row">
          <button type="submit" className="btn btn-primary" disabled={addTargets.isPending}>
            {addTargets.isPending ? 'Wird hinzugefügt …' : 'Hinzufügen'}
          </button>
        </div>
      </form>

      <div className="field-row" style={{ marginTop: '16px' }}>
        <label className="field">
          <span>Alle Ankünfte verschieben (Minuten, positiv = früher)</span>
          <input type="number" value={shiftMinutes} onChange={(e) => setShiftMinutes(e.target.value)} />
        </label>
        <button type="button" className="btn" disabled={shiftArrivals.isPending} onClick={handleShift}>
          Verschieben
        </button>
      </div>

      {targets.length === 0 && <p className="muted center pad">Noch keine Zieldörfer angelegt.</p>}

      {targets.map((target) => (
        <TargetCard
          key={target.id}
          target={target}
          templates={templates}
          lastWaveArrivalStart={lastWaveArrivalStart}
          lastWaveArrivalEnd={lastWaveArrivalEnd}
          onDeleteTarget={() => deleteTarget.mutate({ targetId: target.id }, { onError: (err) => toast.push('error', err.message) })}
          onDeleteWave={(waveId) => deleteWave.mutate({ targetId: target.id, waveId }, { onError: (err) => toast.push('error', err.message) })}
          onAddWave={(input) =>
            addWave.mutate(
              { targetId: target.id, ...input },
              { onSuccess: () => toast.push('success', 'Welle hinzugefügt.'), onError: (err) => toast.push('error', err.message) },
            )
          }
          addWavePending={addWave.isPending}
        />
      ))}
    </section>
  );
}

function TargetCard({
  target,
  templates,
  lastWaveArrivalStart,
  lastWaveArrivalEnd,
  onDeleteTarget,
  onDeleteWave,
  onAddWave,
  addWavePending,
}: {
  target: AttackPlanTarget;
  templates: Array<{ id: number; name: string }>;
  lastWaveArrivalStart: number | null;
  lastWaveArrivalEnd: number | null;
  onDeleteTarget: () => void;
  onDeleteWave: (waveId: number) => void;
  onAddWave: (input: { packageTemplateId: number; count: number; arrivalTimestamp: number; arrivalEnd: number | null; critical: boolean }) => void;
  addWavePending: boolean;
}) {
  const [showForm, setShowForm] = useState(false);
  const [packageTemplateId, setPackageTemplateId] = useState('');
  const [count, setCount] = useState('1');
  // Vorbelegt mit der zuletzt benutzten Ankunftszeitspanne, siehe TargetsSection.
  const [arrivalAt, setArrivalAt] = useState(() => toDatetimeLocal(lastWaveArrivalStart));
  const [arrivalEndAt, setArrivalEndAt] = useState(() => toDatetimeLocal(lastWaveArrivalEnd));
  const [critical, setCritical] = useState(false);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const arrivalTimestamp = fromDatetimeLocal(arrivalAt);
    if (!packageTemplateId || arrivalTimestamp === null) return;
    onAddWave({
      packageTemplateId: Number(packageTemplateId),
      count: Number(count),
      arrivalTimestamp,
      arrivalEnd: fromDatetimeLocal(arrivalEndAt),
      critical,
    });
    setShowForm(false);
    setPackageTemplateId('');
    setCount('1');
    setCritical(false);
  };

  return (
    <div className="card card-callout" style={{ marginTop: '12px' }}>
      <div className="card-head">
        <h3 style={{ margin: 0 }}>
          {target.label} ({target.x}|{target.y})
        </h3>
        <div className="actions">
          <button type="button" className="btn btn-sm" onClick={() => setShowForm((v) => !v)}>
            {showForm ? 'Abbrechen' : '+ Welle'}
          </button>
          <button type="button" className="btn btn-danger btn-sm" onClick={onDeleteTarget}>
            Zieldorf entfernen
          </button>
        </div>
      </div>

      {showForm && (
        <form className="form" onSubmit={handleSubmit} noValidate style={{ marginTop: '8px' }}>
          <div className="field-row">
            <label className="field grow">
              <span>Pakettyp</span>
              <select value={packageTemplateId} onChange={(e) => setPackageTemplateId(e.target.value)} required>
                <option value="" disabled>
                  Pakettyp wählen …
                </option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Anzahl</span>
              <input type="number" min={1} value={count} onChange={(e) => setCount(e.target.value)} required />
            </label>
          </div>
          <div className="field-row">
            <label className="field">
              <span>Ankunft ab</span>
              <input type="datetime-local" step="1" value={arrivalAt} onChange={(e) => setArrivalAt(e.target.value)} required />
            </label>
            <label className="field">
              <span>Ankunft bis (optional)</span>
              <input type="datetime-local" step="1" value={arrivalEndAt} onChange={(e) => setArrivalEndAt(e.target.value)} />
            </label>
          </div>
          <label className="checkbox-field">
            <input type="checkbox" checked={critical} onChange={(e) => setCritical(e.target.checked)} />
            <span>Kritisch</span>
          </label>
          <div className="button-row">
            <button type="submit" className="btn btn-primary btn-sm" disabled={addWavePending}>
              Welle hinzufügen
            </button>
          </div>
        </form>
      )}

      {target.waves.length === 0 && <p className="muted">Noch keine Wellen.</p>}
      {target.waves.length > 0 && (
        <ul>
          {target.waves.map((wave) => {
            const template = templates.find((t) => t.id === wave.packageTemplateId);
            return (
              <li key={wave.id}>
                {wave.count}x {template?.name ?? `Vorlage #${wave.packageTemplateId}`} · Ankunft{' '}
                {new Date(wave.arrivalTimestamp).toLocaleString('de-DE')}
                {wave.arrivalEnd ? ` – ${new Date(wave.arrivalEnd).toLocaleString('de-DE')}` : ''}
                {wave.critical ? ' · kritisch' : ''}{' '}
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => onDeleteWave(wave.id)}>
                  Löschen
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function reasonText(result: WaveResult): string {
  if (result.error) return result.error;
  if (!result.reason) return '';
  const parts: string[] = [];
  if (result.reason.keineTruppen) parts.push(`${result.reason.keineTruppen}x keine Truppen`);
  if (result.reason.zuWeitWeg) parts.push(`${result.reason.zuWeitWeg}x zu weit weg`);
  if (result.reason.ausserhalbFenster) parts.push(`${result.reason.ausserhalbFenster}x außerhalb Abschickzeit`);
  if (result.reason.bereitsVerwendet) parts.push(`${result.reason.bereitsVerwendet}x Dorf bereits verbraucht (Splitting-/Dopplerschutz)`);
  return parts.join(', ');
}

function CheckSection({
  planId,
  worldKey,
  lastCheckedAt,
  toast,
}: {
  planId: number;
  worldKey: string;
  lastCheckedAt: string | null;
  toast: ReturnType<typeof useToast>;
}) {
  const stored = useAttackPlanCheck(worldKey, planId);
  const runCheck = useRunAttackPlanCheck(worldKey, planId);
  const dsWorkbench = useAttackPlanDsWorkbenchExport(worldKey, planId);
  const [exportResult, setExportResult] = useState<DsWorkbenchExportResult | null>(null);
  const [exportMode, setExportMode] = useState<'gesamt' | 'je-spieler'>('gesamt');

  const result = runCheck.data ?? stored.data;

  const handleCheck = () => {
    // Explizite Nutzeranfrage: eine bereits durchgerechnete Planung wird
    // von einer erneuten Prüfung überschrieben (siehe runCheck/
    // replaceCheckResults) -- vorher warnen, statt das stillschweigend zu tun.
    if (
      lastCheckedAt &&
      !window.confirm(
        `Für diesen Plan gibt es bereits eine durchgerechnete Planung vom ${new Date(lastCheckedAt).toLocaleString('de-DE')}. Eine erneute Berechnung überschreibt dieses Ergebnis. Fortfahren?`,
      )
    ) {
      return;
    }
    runCheck.mutate(undefined, { onError: (err) => toast.push('error', err.message) });
  };

  const handleExport = () => {
    setExportResult(null);
    dsWorkbench.mutate(undefined, {
      onSuccess: (data) => {
        setExportResult(data);
        if (data.skipped) toast.push('error', `${data.skipped} Zuweisung(en) ohne auflösbare Dorf-Spiel-ID übersprungen.`);
      },
      onError: (err) => toast.push('error', err.message),
    });
  };

  const copyLines = async (lines: string[]) => {
    if (!lines.length) return;
    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      toast.push('success', 'DS-Workbench-Export in die Zwischenablage kopiert.');
    } catch {
      toast.push('error', 'Zwischenablage nicht verfügbar -- bitte manuell markieren und kopieren.');
    }
  };

  return (
    <section className="card">
      <h2>Verfügbarkeit & Export</h2>
      <p className="muted">
        {lastCheckedAt ? `Zuletzt geprüft am ${new Date(lastCheckedAt).toLocaleString('de-DE')}.` : 'Noch nie geprüft.'}
      </p>

      <div className="button-row">
        <button type="button" className="btn btn-primary" disabled={runCheck.isPending} onClick={handleCheck}>
          {runCheck.isPending ? 'Wird geprüft …' : 'Verfügbarkeit prüfen'}
        </button>
        <button type="button" className="btn" disabled={!lastCheckedAt || dsWorkbench.isPending} onClick={handleExport}>
          {dsWorkbench.isPending ? 'Wird erstellt …' : 'DS-Workbench-Export'}
        </button>
      </div>

      {result && result.waves.length > 0 && (
        <div className="table-wrap" style={{ marginTop: '16px' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Welle</th>
                <th>Zugewiesen / Angefordert</th>
                <th>Grund</th>
              </tr>
            </thead>
            <tbody>
              {result.waves.map((wave) => (
                <tr key={wave.waveId}>
                  <td>#{wave.waveId}{wave.critical ? ' (kritisch)' : ''}</td>
                  <td className={wave.assigned >= wave.requested ? '' : 'muted'}>
                    {wave.assigned} / {wave.requested}
                  </td>
                  <td>{reasonText(wave)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result && result.droppedCriticalTargets.length > 0 && (
        <p className="flash flash-error">
          {result.droppedCriticalTargets.length} Zieldorf/Zieldörfer ohne vollständige kritische Wellen-Serie (komplett zurückgerollt).
        </p>
      )}

      {exportResult && (
        <div style={{ marginTop: '16px' }}>
          <div className="field-row" style={{ alignItems: 'center' }}>
            <span className="muted">Export-Ansicht:</span>
            <label className="checkbox-field">
              <input type="radio" name="export-mode" checked={exportMode === 'gesamt'} onChange={() => setExportMode('gesamt')} />
              <span>Gesamt</span>
            </label>
            <label className="checkbox-field">
              <input type="radio" name="export-mode" checked={exportMode === 'je-spieler'} onChange={() => setExportMode('je-spieler')} />
              <span>Je Spieler gruppiert</span>
            </label>
          </div>

          {exportMode === 'gesamt' && (
            <div className="field" style={{ marginTop: '8px' }}>
              <span>DS-Workbench-Export ({exportResult.lines.length} Zeile(n))</span>
              <textarea rows={6} readOnly value={exportResult.lines.join('\n')} />
              <div className="button-row">
                <button type="button" className="btn btn-sm" onClick={() => copyLines(exportResult.lines)}>
                  Kopieren
                </button>
              </div>
            </div>
          )}

          {exportMode === 'je-spieler' &&
            (exportResult.byPlayer.length === 0 ? (
              <p className="muted">Keine Zuweisungen zum Exportieren.</p>
            ) : (
              exportResult.byPlayer.map((group) => (
                <div key={group.player ?? '__unbekannt__'} className="card card-callout" style={{ marginTop: '8px' }}>
                  <h4 style={{ margin: '0 0 8px' }}>
                    {group.player ?? 'Unbekannter Spieler'} ({group.lines.length} Zeile{group.lines.length === 1 ? '' : 'n'})
                  </h4>
                  <textarea rows={4} readOnly value={group.lines.join('\n')} />
                  <div className="button-row" style={{ marginTop: '8px' }}>
                    <button type="button" className="btn btn-sm" onClick={() => copyLines(group.lines)}>
                      Kopieren
                    </button>
                  </div>
                </div>
              ))
            ))}
        </div>
      )}
    </section>
  );
}
